import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storeAPI, vendorAPI } from '../../services/api';
import { Store, StoreFilters } from '../../types';

interface StoresState {
  stores: Store[];
  currentStore: Store | null;
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  } | null;
}

const initialState: StoresState = {
  stores: [],
  currentStore: null,
  loading: false,
  error: null,
  pagination: null,
};

// Async thunks
export const fetchStores = createAsyncThunk(
  'stores/fetchStores',
  async (filters: StoreFilters = {}, { rejectWithValue }) => {
    try {
      const params: Record<string, string | number> = {
        page: filters.page ?? 1,
        limit: filters.limit ?? 10,
        sortBy: filters.sortBy ?? 'createdAt',
        sortOrder: filters.sortOrder ?? 'desc',
      };
      if (filters.status) params.status = filters.status;
      if (filters.search?.trim()) params.search = filters.search.trim();

      const response = await vendorAPI.getStores(params);
      const body = response.data as any;
      return {
        stores: body.data?.stores ?? [],
        pagination: body.pagination ?? null,
      };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch stores';
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchStoreById = createAsyncThunk(
  'stores/fetchStoreById',
  async (storeId: string, { rejectWithValue }) => {
    try {
      const response = await storeAPI.getById(storeId);
      return (response.data as any).data.store;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch store';
      return rejectWithValue(errorMessage);
    }
  }
);

export const createStore = createAsyncThunk(
  'stores/createStore',
  async (storeData: any, { rejectWithValue }) => {
    try {
      const response = await storeAPI.create(storeData);
      return (response.data as any).data.store;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to create store';
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateStore = createAsyncThunk(
  'stores/updateStore',
  async ({ storeId, storeData }: { storeId: string; storeData: any }, { rejectWithValue }) => {
    try {
      const response = await storeAPI.update(storeId, storeData);
      return (response.data as any).data.store;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to update store';
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteStore = createAsyncThunk(
  'stores/deleteStore',
  async (storeId: string, { rejectWithValue }) => {
    try {
      await storeAPI.delete(storeId);
      return storeId;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to delete store';
      return rejectWithValue(errorMessage);
    }
  }
);

export const uploadStoreImages = createAsyncThunk(
  'stores/uploadStoreImages',
  async ({ storeId, formData }: { storeId: string; formData: FormData }, { rejectWithValue }) => {
    try {
      const response = await storeAPI.uploadImages(storeId, formData);
      return (response.data as any).data.store;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to upload images';
      return rejectWithValue(errorMessage);
    }
  }
);

const storesSlice = createSlice({
  name: 'stores',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentStore: (state, action) => {
      state.currentStore = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch stores
      .addCase(fetchStores.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.loading = false;
        state.stores = action.payload.stores;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch store by ID
      .addCase(fetchStoreById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStoreById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentStore = action.payload;
        state.error = null;
      })
      .addCase(fetchStoreById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create store
      .addCase(createStore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStore.fulfilled, (state, action) => {
        state.loading = false;
        state.stores.unshift(action.payload);
        state.error = null;
      })
      .addCase(createStore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update store
      .addCase(updateStore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStore.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.stores.findIndex(store => store._id === action.payload._id);
        if (index !== -1) {
          state.stores[index] = action.payload;
        }
        if (state.currentStore?._id === action.payload._id) {
          state.currentStore = action.payload;
        }
        state.error = null;
      })
      .addCase(updateStore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete store
      .addCase(deleteStore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteStore.fulfilled, (state, action) => {
        state.loading = false;
        state.stores = state.stores.filter(store => store._id !== action.payload);
        if (state.currentStore?._id === action.payload) {
          state.currentStore = null;
        }
        state.error = null;
      })
      .addCase(deleteStore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Upload store images
      .addCase(uploadStoreImages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadStoreImages.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.stores.findIndex(store => store._id === action.payload._id);
        if (index !== -1) {
          state.stores[index] = action.payload;
        }
        if (state.currentStore?._id === action.payload._id) {
          state.currentStore = action.payload;
        }
        state.error = null;
      })
      .addCase(uploadStoreImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentStore } = storesSlice.actions;
export default storesSlice.reducer;
